import { formatDate } from '@crm/shared';
import { Button, Card, CardContent, Spinner } from '@crm/ui';
import { Contact, Pencil, Plus, Trash2, Upload, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog, EmptyState, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { CAMPAIGNS_ROUTES } from '@/modules/campaigns/api';
import { type ContactList, useContactLists, useDeleteContactList } from '../api';
import { ContactListModal } from '../components/contact-list-modal';

/**
 * Publicos com nome, prontos para as campanhas.
 *
 * A lista guarda o criterio, e nao as pessoas: "clientes de Pinheiros" precisa
 * valer para quem chegou ontem, e uma lista congelada envelheceria sozinha.
 * Por isso o total aparece ao lado do nome - e o de hoje, contado na hora.
 */
export function ContactListsPage() {
  useDocumentTitle('Listas de contatos');

  const canWrite = useCan('customers', 'WRITE');
  const listas = useContactLists();
  const apagar = useDeleteContactList();

  const [editando, setEditando] = useState<ContactList | 'nova' | null>(null);
  const [apagando, setApagando] = useState<ContactList | null>(null);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<Contact />}
        title="Listas de contatos"
        description="Publicos com nome, para nao remontar os mesmos filtros em toda campanha."
        actions={
          canWrite ? (
            <>
              <Button variant="outline" asChild>
                <Link to={CAMPAIGNS_ROUTES.IMPORT}>
                  <Upload className="h-4 w-4" />
                  <span>
                    Importar<span className="hidden sm:inline"> contatos</span>
                  </span>
                </Link>
              </Button>
              <Button onClick={() => setEditando('nova')}>
                <Plus className="h-4 w-4" />
                <span>
                  Nova<span className="hidden sm:inline"> lista</span>
                </span>
              </Button>
            </>
          ) : null
        }
      />

      {apagar.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(apagar.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="py-2">
          {listas.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : (listas.data?.data ?? []).length > 0 ? (
            <ul className="divide-y divide-border">
              {(listas.data?.data ?? []).map((lista) => (
                <li key={lista.id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Users className="h-5 w-5" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{lista.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {lista.total} contatos - criada em {formatDate(lista.createdAt)}
                    </p>
                  </div>

                  {canWrite ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        aria-label={`Alterar ${lista.name}`}
                        onClick={() => setEditando(lista)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`Apagar ${lista.name}`}
                        onClick={() => setApagando(lista)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Contact />} title="Nenhuma lista por aqui" />
          )}
        </CardContent>
      </Card>

      {editando ? (
        <ContactListModal
          list={editando === 'nova' ? undefined : editando}
          onClose={() => setEditando(null)}
          onSaved={() => setEditando(null)}
        />
      ) : null}

      <ConfirmDialog
        open={apagando !== null}
        title="Apagar a lista?"
        message="As campanhas que ja usaram o recorte continuam como estao - elas guardam o criterio, e nao a lista."
        confirmLabel="Apagar"
        loading={apagar.isPending}
        onConfirm={() => {
          if (apagando) apagar.mutate(apagando.id);
          setApagando(null);
        }}
        onClose={() => setApagando(null)}
      />
    </div>
  );
}
